import { boundedOrderRead } from '@/hooks/useOrderWorkspaceSession';

// TCA-26101907: renderer-lifetime custody only. Claims survive a screen opening,
// never a restart. Unknown effects remain held; a fresh read is not a receipt.
const claims = new Map();
const observers = new Set();
const changed = () => observers.forEach(listener => { try { listener(); } catch { /* Observation cannot abandon settlement. */ } });
const bindingOf = response => {
  const value = response.headers?.get?.('X-LVB-Cache-Binding') ?? response.headers?.['x-lvb-cache-binding'];
  if (value != null && (typeof value !== 'string' || !/^[a-f0-9]{64}$/.test(value))) throw new Error('Invalid cache identity');
  return value ?? null;
};
const refusal = error => !['offline_run_acceptance_unavailable', 'offline_run_acceptance_unknown']
  .includes(error?.response?.data?.detail?.code)
  && [400, 401, 403, 404, 409, 422].includes(error?.response?.status);

export function createReviewOwner(session) {
  let active = false;
  let sequence = 0;
  let observation = null;
  let admitted = new WeakSet();
  const controllers = new Set();
  const opening = {};
  const pair = () => {
    const cloud = session.capture();
    const local = session.capture(true);
    return cloud && local && !cloud.offline ? { cloud, local, current: () => cloud.current() && local.current() } : null;
  };
  const alive = () => active && !!pair();
  const resource = scope => JSON.stringify([scope.local.endpoint, scope.local.tenant]);
  const localConfig = (scope, binding) => ({ ...scope.local.config,
    reviewLocalBase: scope.local.endpoint.replace(/\/api$/, ''),
    headers: { ...scope.local.config.headers, ...(binding ? { 'X-LVB-Cache-Binding': binding } : {}) } });
  const related = () => {
    const scope = pair();
    return scope ? [...claims.values()].filter(claim => claim.resource === resource(scope)) : [];
  };
  const assert = predicate => { if (!predicate) throw new Error('Review context changed'); };
  const owner = {
    opening,
    activate() { active = true; },
    retire() { active = false; sequence++; admitted = new WeakSet(); controllers.forEach(controller => controller.abort()); },
    current: alive,
    subscribe(listener) { observers.add(listener); return () => observers.delete(listener); },
    holds() { return related().filter(claim => claim.state !== 'pending'); },
    pending() { return related().some(claim => claim.state === 'pending'); },
    captureRunSend(item) {
      const captured = observation;
      if (!alive() || !captured || !captured.scope.current() || !admitted.has(item)
        || !captured.scope.cloud.admin) return null;
      return { local: localConfig(captured.scope, captured.binding), cloud: captured.scope.cloud.config, tenant: captured.scope.local.tenant,
        current: () => captured.scope.current(),
        presentationCurrent: () => alive() && captured.scope.current(),
        dispatchCurrent: () => alive() && captured.scope.current() && observation === captured && sequence === captured.revision };
    },
    beginRead() {
      controllers.forEach(controller => controller.abort());
      const scope = pair();
      const revision = ++sequence;
      admitted = new WeakSet();
      if (!active || !scope) return null;
      let binding;
      const current = () => alive() && scope.current() && sequence === revision;
      const acceptBinding = response => {
        const next = bindingOf(response);
        if (binding !== undefined && binding !== next) throw new Error('Review cache changed during loading');
        binding = next;
      };
      const read = async (fn, args, local) => {
        assert(current());
        const config = local ? { ...localConfig(scope, binding), reviewResponse: acceptBinding } : scope.cloud.config;
        const value = await boundedOrderRead(options => fn(...args, options), config, controllers);
        assert(current());
        return value;
      };
      return {
        current,
        local: (fn, ...args) => read(fn, args, true),
        cloud: (fn, ...args) => read(fn, args, false),
        async evidence(fn, ...args) {
          assert(current());
          const value = await fn(...args, localConfig(scope, binding));
          assert(current());
          return value;
        },
        publish(groups) {
          if (!current() || groups.some(group => !Array.isArray(group))) return false;
          const rows = groups.flat();
          if (rows.some(row => !row || typeof row !== 'object')) return false;
          admitted = new WeakSet(rows.flatMap(row => [row, row.order].filter(Boolean)));
          observation = { scope, binding: binding ?? null, revision };
          return true;
        },
      };
    },
    async execute(item, { key, admin = true, cloud, local, read, prepare, start, finish, validateCloud, validateLocal, refused }) {
      const captured = observation;
      if (!alive() || !captured || !captured.scope.current() || !admitted.has(item)
        || (admin && !captured.scope.cloud.admin)) return { state: 'blocked' };
      const scope = captured.scope;
      const target = `${resource(scope)}:${captured.binding || 'legacy'}:${key}`;
      if (claims.has(target)) return { state: 'blocked' };
      const claim = { resource: resource(scope), target, state: 'pending', session: session.key, binding: captured.binding };
      claims.set(target, claim); changed(); start?.();
      let dispatched = false;
      let phase = 'read';
      let cloudValue;
      const current = () => alive() && scope.current();
      const publishable = () => current() && observation === captured && sequence === captured.revision;
      const dispatch = async (fn, config, value) => {
        assert(scope.current());
        dispatched = true;
        return fn(config, value);
      };
      try {
        if (read) {
          const value = await boundedOrderRead(config => read(config), scope.cloud.config, controllers);
          claims.delete(target);
          return { state: publishable() ? 'read' : 'retired', value };
        }
        let prepared;
        if (prepare) {
          phase = 'prepare';
          prepared = await dispatch(prepare, localConfig(scope, captured.binding));
        }
        if (cloud) {
          phase = 'cloud';
          cloudValue = await dispatch(cloud, scope.cloud.config, prepared);
          if (validateCloud && !validateCloud(cloudValue)) throw new Error('Cloud acknowledgement is invalid');
          phase = 'local';
          // Keep only the exact local continuation. Never repeat the cloud write
          // after a known reply, and never refresh the binding to a new cache.
          claim.retry = async () => {
            assert(scope.current());
            const value = await local(localConfig(scope, captured.binding), cloudValue);
            if (validateLocal && !validateLocal(value)) throw new Error('Local acknowledgement is invalid');
            return value;
          };
          claim.authorized = () => scope.current();
        } else phase = 'local';
        const value = await dispatch(local, localConfig(scope, captured.binding), cloudValue);
        if (validateLocal && !validateLocal(value)) throw new Error('Local acknowledgement is invalid');
        claims.delete(target);
        return { state: publishable() ? 'accepted' : 'retired', accepted: true, value, cloudValue };
      } catch (error) {
        if (phase === 'cloud' && refusal(error) && refused && scope.current()) {
          try { await refused(localConfig(scope, captured.binding), error); } catch { /* Original refusal remains visible. */ }
        }
        if (phase === 'read' || !dispatched || (phase === 'prepare' && refusal(error)) || (phase === 'cloud' && refusal(error))
          || (!cloud && refusal(error))) claims.delete(target);
        else claim.state = phase === 'local' && cloud ? 'local-pending' : 'unknown';
        return { state: publishable() ? claim.state === 'local-pending' ? 'local-pending'
          : claims.has(target) ? 'unknown' : 'refused' : 'retired', error };
      } finally { if (current()) finish?.(); changed(); }
    },
    canRetry(claim) {
      return alive() && claim.session === session.key && !!claim.authorized?.() && !!claim.retry
        && claim.state === 'local-pending' && observation?.binding === claim.binding;
    },
    async retry(claim) {
      if (!owner.canRetry(claim)) return false;
      claim.state = 'pending'; changed();
      try { await claim.retry(); claims.delete(claim.target); return alive(); }
      catch { claim.state = 'local-pending'; return false; }
      finally { changed(); }
    },
  };
  return owner;
}
